import { createSlice } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import { createQuiz, getQuizzes, updateQuiz, addQuestion, updateQuestion, removeQuestion, deleteQuiz, updateQuizStatus, getQuiz, } from '../actions/quizzes';
const initialState = {
    quizzes: [],
    activeFilter: {},
    quizDetail: null,
    loading: false,
    error: false,
    errorDetail: '',
    lastCreatedQuizId: null,
    editingQuestionId: null,
};
const quizzesSlice = createSlice({
    name: 'quizzes',
    initialState,
    reducers: {
        setActiveFilter: (state, action) => {
            state.activeFilter = action.payload;
        },
        setEditingQuestionId: (state, action) => {
            state.editingQuestionId = action.payload.questionId || null;
        },
        setlastCreatedQuizId: (state, action) => {
            state.lastCreatedQuizId = action.payload.quizId || null;
        },
        clearError: (state, _action) => {
            state.error = false;
            state.errorDetail = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getQuizzes.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(getQuizzes.fulfilled, (state, action) => {
            state.quizzes = action.payload.quizzes.map((quiz) => ({
                ...quiz,
                questions: [],
            }));
            state.loading = false;
            state.error = false;
        })
            .addCase(getQuizzes.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        })
            .addCase(createQuiz.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(createQuiz.fulfilled, (state, action) => {
            const quiz = action.payload.quiz;
            const newQuiz = {
                quizId: quiz.quizId || v4(),
                name: quiz.name || '',
                duration: quiz.duration || '',
                status: 'draft',
                questionsCount: 0,
                submissionsCount: 0,
            };
            state.quizzes = [...state.quizzes, newQuiz];
            state.loading = false;
            state.error = false;
            state.lastCreatedQuizId = newQuiz.quizId;
        })
            .addCase(createQuiz.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        })
            .addCase(updateQuiz.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(updateQuiz.fulfilled, (state, action) => {
            const { quizId, name, duration, settings } = action.payload;
            const quiz = state.quizzes.find((quiz) => quiz.quizId === quizId);
            if (quiz) {
                quiz.name = name;
                quiz.duration = duration;
            }
            if (state.quizDetail) {
                state.quizDetail.name = name;
                state.quizDetail.duration = duration;
                state.quizDetail.settings = settings;
            }
            state.loading = false;
            state.error = false;
        })
            .addCase(updateQuiz.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        })
            .addCase(getQuiz.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(getQuiz.fulfilled, (state, action) => {
            state.quizDetail = action.payload.quiz;
            state.loading = false;
            state.error = false;
        })
            .addCase(getQuiz.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        })
            .addCase(updateQuizStatus.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(updateQuizStatus.fulfilled, (state, action) => {
            const { quizId, status, code } = action.payload;
            const quiz = state.quizzes.find((quiz) => quiz.quizId === quizId);
            if (quiz) {
                quiz.status = status;
                quiz.code = code;
            }
            if (state.quizDetail) {
                state.quizDetail.code = code;
                state.quizDetail.status = status;
            }
            state.loading = false;
            state.error = false;
        })
            .addCase(updateQuizStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        })
            .addCase(deleteQuiz.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(deleteQuiz.fulfilled, (state, action) => {
            const { quizId } = action.payload;
            state.quizzes = state.quizzes.filter((quiz) => quiz.quizId !== quizId);
            state.loading = false;
            state.error = false;
        })
            .addCase(deleteQuiz.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        })
            .addCase(addQuestion.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(addQuestion.fulfilled, (state, action) => {
            const { question, quizId } = action.payload;
            if (state.quizDetail)
                state.quizDetail = {
                    ...state.quizDetail,
                    questions: [...state.quizDetail.questions, question],
                };
            const quiz = state.quizzes.find((quiz) => quiz.quizId === quizId);
            if (quiz) {
                quiz.questionsCount += 1;
            }
            state.loading = false;
            state.error = false;
        })
            .addCase(addQuestion.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        })
            .addCase(updateQuestion.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(updateQuestion.fulfilled, (state, action) => {
            const { quizId, question } = action.payload;
            if (state.quizDetail) {
                state.quizDetail.questions = state.quizDetail.questions.map((que) => que.questionId === question.questionId
                    ? { ...que, ...question }
                    : que);
            }
            state.loading = false;
            state.error = false;
            state.editingQuestionId = null;
        })
            .addCase(updateQuestion.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        })
            .addCase(removeQuestion.pending, (state, _action) => {
            state.loading = true;
            state.error = false;
        })
            .addCase(removeQuestion.fulfilled, (state, action) => {
            const { quizId, questionId } = action.payload;
            if (state.quizDetail)
                state.quizDetail.questions = state.quizDetail.questions.filter((q) => q.questionId !== questionId);
            const quiz = state.quizzes.find((quiz) => quiz.quizId === quizId);
            if (quiz) {
                quiz.questionsCount -= 1;
            }
            state.loading = false;
            state.error = false;
            state.editingQuestionId = null;
        })
            .addCase(removeQuestion.rejected, (state, action) => {
            state.loading = false;
            state.error = true;
            state.errorDetail = action.payload || 'An error occurred';
        });
    },
});
export const { setActiveFilter, setEditingQuestionId, setlastCreatedQuizId, clearError, } = quizzesSlice.actions;
export { quizzesSlice };
